import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ExternalLink, Upload, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const Section = ({ title, desc, children }) => (
  <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm md:p-8">
    <h2 className="text-lg font-bold text-gray-900">{title}</h2>
    {desc && <p className="mt-1 text-sm text-gray-500">{desc}</p>}
    <div className="mt-6 space-y-5">{children}</div>
  </section>
);

const Field = ({ label, hint, children }) => (
  <div>
    <Label className="text-gray-700">{label}</Label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
);

const ImageUploader = ({ label, index, path, onUpload, accept = "image/*" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpload(data.path);
      toast.success(`${label} uploaded`);
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <Label className="text-gray-700">{label}</Label>
      <div className="mt-2 flex items-center gap-4">
        <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-50">
          {path ? (
            <img src={fileUrl(path)} alt={label} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-gray-300">
              <Plus className="h-5 w-5" />
            </div>
          )}
        </div>
        <input ref={inputRef} type="file" accept={accept} onChange={handleFile} className="hidden" data-testid={`upload-input-${index}`} />
        <Button type="button" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`upload-btn-${index}`}>
          <Upload className="mr-2 h-4 w-4" />
          {busy ? "Uploading…" : path ? "Replace" : "Upload"}
        </Button>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  useEffect(() => {
    api.get("/content").then(({ data }) => setForm((prev) => prev ?? data)).catch(() => {});
  }, []);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const keys = [
        "brand_name", "hero_title", "hero_description", "apply_label", "apply_url", "hero_image_path",
        "about_heading", "about_text", "image1_path", "image2_path", "favicon_path",
        "contact_email", "instagram_url", "meta_description", "share_image_path",
      ];
      const payload = Object.fromEntries(keys.map((k) => [k, form[k] ?? ""]));
      payload.links = (form.links || []).filter((l) => (l.label || "").trim() && (l.url || "").trim());
      const { data } = await api.put("/content", payload);
      setForm(data);
      toast.success("Changes saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!user || !form) return <div className="min-h-screen bg-gray-50" />;

  const input = (key, placeholder) => (
    <Input
      data-testid={`${key.replace(/_/g, "-")}-input`}
      value={form[key] || ""}
      onChange={(e) => set(key, e.target.value)}
      placeholder={placeholder}
      className="mt-2"
    />
  );

  const area = (key, rows = 3) => (
    <Textarea
      data-testid={`${key.replace(/_/g, "-")}-input`}
      value={form[key] || ""}
      onChange={(e) => set(key, e.target.value)}
      rows={rows}
      className="mt-2"
    />
  );

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400">Admin</p>
            <h1 className="font-display text-lg font-extrabold uppercase tracking-tight">Jack Heffernan SC</h1>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noopener noreferrer">
              <Button variant="ghost" size="sm" data-testid="view-site-btn">
                <ExternalLink className="mr-2 h-4 w-4" /> View site
              </Button>
            </a>
            <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn">
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-6 px-6 py-10">
        <Section title="Brand" desc="Shown in the header and footer.">
          <Field label="Brand name">{input("brand_name", "Jack Heffernan SC")}</Field>
        </Section>

        <Section title="Browser Tab Icon" desc="The small icon shown next to your site name in the browser tab. Upload a square image (PNG, JPG, ICO or SVG).">
          <ImageUploader
            label="Favicon"
            index="favicon"
            path={form.favicon_path}
            onUpload={(p) => set("favicon_path", p)}
            accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon,image/vnd.microsoft.icon,.ico"
          />
        </Section>

        <Section title="Google & Link Sharing" desc="How your site appears in Google results and when your link is shared on WhatsApp or iMessage.">
          <Field label="Search description" hint="Shown under your site name in Google. Keep it to 1–2 short sentences.">
            {area("meta_description", 2)}
          </Field>
          <ImageUploader
            label="Share image"
            index="share"
            path={form.share_image_path}
            onUpload={(p) => set("share_image_path", p)}
            accept="image/png,image/jpeg,image/webp"
          />
        </Section>

        <Section title="Hero — 1:1 Online Coaching" desc="The main heading, what's included, the button label and the questionnaire link.">
          <Field label="Heading">{input("hero_title", "1:1 Online Coaching")}</Field>
          <Field label="What's included" hint='Start a line with "- " to show it as a bullet point.'>
            {area("hero_description", 6)}
          </Field>
          <Field label="Apply button label">{input("apply_label", "Apply for 1:1 Coaching")}</Field>
          <Field label="Questionnaire link" hint="e.g. https://forms.gle/... or https://...typeform.com/...">
            {input("apply_url", "https://forms.gle/...")}
          </Field>
        </Section>

        <Section title="About" desc="Section title, text and two photos.">
          <Field label="Section title">{input("about_heading", "About")}</Field>
          <Field label="Text">{area("about_text", 4)}</Field>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <ImageUploader label="Photo 1" index="1" path={form.image1_path} onUpload={(p) => set("image1_path", p)} />
            <ImageUploader label="Photo 2" index="2" path={form.image2_path} onUpload={(p) => set("image2_path", p)} />
          </div>
        </Section>

        <Section title="Footer" desc="Your email, Instagram and any other links shown at the bottom of the page.">
          <Field label="Contact email">{input("contact_email", "jack@example.com")}</Field>
          <Field label="Instagram URL">{input("instagram_url", "https://instagram.com/...")}</Field>
          <div>
            <Label className="text-gray-700">Links</Label>
            <div className="mt-2 space-y-3">
              {(form.links || []).map((link, i) => (
                <div key={link.id || i} className="flex items-center gap-2">
                  <Input
                    data-testid={`link-label-${i + 1}`}
                    value={link.label || ""}
                    onChange={(e) => set("links", (form.links || []).map((l, idx) => (idx === i ? { ...l, label: e.target.value } : l)))}
                    placeholder="Label (e.g. Apply)"
                  />
                  <Input
                    data-testid={`link-url-${i + 1}`}
                    value={link.url || ""}
                    onChange={(e) => set("links", (form.links || []).map((l, idx) => (idx === i ? { ...l, url: e.target.value } : l)))}
                    placeholder="https://..."
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-testid={`link-remove-${i + 1}`}
                    onClick={() => set("links", (form.links || []).filter((_, idx) => idx !== i))}
                  >
                    <Trash2 className="h-4 w-4 text-gray-400" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="link-add-btn"
                onClick={() => set("links", [...(form.links || []), { id: crypto.randomUUID(), label: "", url: "" }])}
              >
                <Plus className="mr-2 h-4 w-4" /> Add link
              </Button>
            </div>
          </div>
        </Section>

        <div className="sticky bottom-4 flex justify-end">
          <Button
            onClick={save}
            disabled={saving}
            data-testid="save-btn"
            className="rounded-full bg-gray-900 px-8 py-6 font-semibold text-white shadow-lg hover:bg-gray-800"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </main>
    </div>
  );
}
